import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Plus, Pencil, Trash2, Upload, Eye, EyeOff, X, Save, Image } from 'lucide-react';

const BannerManager = () => {
  const { api } = useAuth();
  const [banners, setBanners] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    name: '', image_url: '', position: 'top', target_url: '',
    alt_text: '', active: true, order: 0
  });

  const load = () => api.get('/admin/banners').then(r => setBanners(r.data || [])).catch(console.error);
  useEffect(() => { load(); }, []);

  const resetForm = () => {
    setForm({ name: '', image_url: '', position: 'top', target_url: '', alt_text: '', active: true, order: 0 });
    setEditing(null); setShowForm(false);
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await api.post('/admin/upload/banner', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(prev => ({ ...prev, image_url: res.data.url }));
    } catch (err) { alert('Upload failed'); }
    setUploading(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editing) { await api.put(`/admin/banners/${editing}`, form); }
      else { await api.post('/admin/banners', form); }
      resetForm(); load();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const handleEdit = (b) => {
    setForm({ name: b.name, image_url: b.image_url || '', position: b.position, target_url: b.target_url || '', alt_text: b.alt_text || '', active: b.active !== false, order: b.order || 0 });
    setEditing(b.id); setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this banner?')) return;
    await api.delete(`/admin/banners/${id}`); load();
  };

  const positions = [
    { value: 'top', label: 'Top Banner', desc: 'Above video grid on homepage' },
    { value: 'between', label: 'Between Grid', desc: 'Between video cards' },
    { value: 'sidebar', label: 'Sidebar', desc: 'Right column on desktop' },
    { value: 'below-player', label: 'Below Player', desc: 'Under video player' },
    { value: 'preroll', label: 'Pre-roll', desc: 'Before video plays (5s skip)' },
    { value: 'popup', label: 'Popup', desc: 'Fullscreen overlay' },
  ];

  const getPosLabel = (pos) => {
    const p = positions.find(pp => pp.value === pos);
    return p ? p.label : pos;
  };

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2 className="section-title"><Image size={20} /> Banner & Ads ({banners.length})</h2>
        <button className="btn-primary" onClick={() => { resetForm(); setShowForm(true); }}><Plus size={16} /> Add Banner</button>
      </div>

      {showForm && (
        <div className="admin-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 className="card-subtitle" style={{ margin: 0 }}>{editing ? 'Edit Banner' : 'Create New Banner'}</h3>
            <button className="btn-sm btn-delete" onClick={resetForm}><X size={14} /></button>
          </div>
          <form onSubmit={handleSave}>
            <div className="two-col">
              <div>
                <label className="form-label">Banner Name *</label>
                <input className="form-input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required placeholder="e.g. Homepage Top Ad" />
              </div>
              <div>
                <label className="form-label">Position *</label>
                <div style={{fontSize:10,color:'#f59e0b',marginBottom:4,padding:'6px 10px',background:'rgba(245,158,11,0.06)',borderRadius:6,lineHeight:1.6}}>
                  📐 <strong>Ukuran Banner:</strong> Top/Below/Between: 728×90px | Sidebar: 300×250px | Popup: 500×400px | Preroll: 640×360px | Download: 468×60px
                </div>
                <select className="form-select" value={form.position} onChange={e => setForm({...form, position: e.target.value})}>
                  {positions.map(p => <option key={p.value} value={p.value}>{p.label} — {p.desc}</option>)}
                </select>
              </div>
            </div>

            <div style={{ marginTop: 12 }}>
              <label className="form-label">Banner Image *</label>
              <input type="file" accept="image/*" onChange={handleUpload} className="form-input" disabled={uploading} />
              {uploading && <p style={{ fontSize: 12, color: '#f59e0b', marginTop: 4 }}>Uploading...</p>}
              {form.image_url && (
                <div style={{ marginTop: 8 }}>
                  <img src={form.image_url} alt="Preview" style={{ maxWidth: '100%', maxHeight: 150, borderRadius: 8, border: '1px solid rgba(255,255,255,0.06)' }} />
                </div>
              )}
            </div>

            <div className="two-col" style={{ marginTop: 12 }}>
              <div>
                <label className="form-label">Target URL (click destination)</label>
                <input className="form-input" value={form.target_url} onChange={e => setForm({...form, target_url: e.target.value})} placeholder="https://example.com" />
              </div>
              <div>
                <label className="form-label">Alt Text (SEO)</label>
                <input className="form-input" value={form.alt_text} onChange={e => setForm({...form, alt_text: e.target.value})} placeholder="Banner description" />
              </div>
            </div>

            <div className="two-col" style={{ marginTop: 12 }}>
              <div>
                <label className="form-label">Display Order</label>
                <input className="form-input" type="number" value={form.order} onChange={e => setForm({...form, order: parseInt(e.target.value) || 0})} />
                <span style={{ fontSize: 10, color: '#555' }}>Lower numbers appear first</span>
              </div>
              <div>
                <div className="toggle-wrap" style={{ marginTop: 20 }}>
                  <button type="button" className={`toggle-switch ${form.active ? 'active' : ''}`} onClick={() => setForm({...form, active: !form.active})} />
                  <span className="toggle-label">Active (Show on website)</span>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button type="submit" className="btn-primary"><Save size={16} /> {editing ? 'Update' : 'Create Banner'}</button>
              <button type="button" className="btn-secondary" onClick={resetForm}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Banner List */}
      {banners.length === 0 ? (
        <div className="admin-card" style={{ textAlign: 'center', padding: 40 }}>
          <Upload size={40} style={{ color: '#555', marginBottom: 10 }} />
          <p style={{ color: '#888', fontSize: 14 }}>No banners yet</p>
          <p style={{ color: '#555', fontSize: 12 }}>Create your first banner to start displaying ads</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 14 }}>
          {banners.map(b => (
            <div key={b.id} className="admin-card" style={{ padding: 0, overflow: 'hidden' }}>
              {b.image_url ? (
                <img src={b.image_url} alt={b.alt_text || b.name} style={{ width: '100%', height: 150, objectFit: 'cover' }} />
              ) : (
                <div style={{ width: '100%', height: 150, background: 'rgba(255,255,255,0.03)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#555' }}>No Image</div>
              )}
              <div style={{ padding: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <h4 style={{ fontSize: 14, fontWeight: 700, color: '#ddd', margin: 0 }}>{b.name}</h4>
                  <span className={`badge ${b.active !== false ? 'badge-success' : 'badge-danger'}`}>
                    {b.active !== false ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 6, fontSize: 11, color: '#555', marginBottom: 8 }}>
                  <span className="badge badge-info">{getPosLabel(b.position)}</span>
                  {b.clicks > 0 && <span>Clicks: {b.clicks}</span>}
                </div>
                {b.target_url && <p style={{ fontSize: 11, color: '#666', marginBottom: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.target_url}</p>}
                <div style={{ display: 'flex', gap: 4 }}>
                  <button className="btn-sm btn-edit" onClick={() => handleEdit(b)}><Pencil size={12} /> Edit</button>
                  <button className="btn-sm btn-delete" onClick={() => handleDelete(b.id)}><Trash2 size={12} /> Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BannerManager;
